import { useMemo } from 'react';
import Course, { CourseProps } from './Course';
import { CoursesBySemestersExtraCourse, SemesterCourse } from '../types';
import {
  CourseStatusTooltip,
  OptionalCourseStatusTooltip,
} from '../../../views/CurriculumProgressStudent/types/progress';
import {
  CourseEnrollmentStates,
  CourseValidationType,
} from '../../../types/CourseValidationType';

export interface CoursesBySemesterProps {
  title: string;
  texts: {
    credits: string;
    optionalToolTip: (data: OptionalCourseStatusTooltip) => string;
    convalidationToolTip: (data: CourseStatusTooltip) => string;
    degreeActivityType?: string;
    degreeActivityTooltip?: string;
  };
  getColor: (
    state?: CourseEnrollmentStates | CourseValidationType,
  ) => string | undefined;
  onClick?: (semesterCourse: SemesterCourse) => void;
  onClickInfoModal?: (row: SemesterCourse) => void;
  semesterCourses: SemesterCourse[];
  extraCourses?: CoursesBySemestersExtraCourse[];
}

export default function CoursesBySemester({
  title,
  texts,
  getColor,
  onClick: onClickProp,
  onClickInfoModal,
  semesterCourses,
  extraCourses,
}: CoursesBySemesterProps) {
  const courses = useMemo<Array<Omit<CourseProps, 'texts'>>>(() => {
    return semesterCourses.map((semesterCourse) => {
      let onClick = undefined;
      if (
        onClickProp &&
        (!semesterCourse.state ||
          semesterCourse.state === CourseEnrollmentStates.Reject)
      ) {
        onClick = () => onClickProp(semesterCourse);
      }
      if (
        onClickInfoModal &&
        semesterCourse.state === CourseEnrollmentStates.Approved &&
        semesterCourse.isValidated
      ) {
        onClick = () => onClickInfoModal(semesterCourse);
      }

      if (semesterCourse.course) {
        return {
          id: semesterCourse.id.toString(),
          name: semesterCourse.course.name,
          code: semesterCourse.course.code,
          credits: semesterCourse.course.credits,
          color: getColor(
            semesterCourse.state === CourseEnrollmentStates.Approved &&
              semesterCourse.isValidated
              ? semesterCourse?.validation?.validationType ??
                  CourseValidationType.Internal
              : semesterCourse.state,
          ),
          examGrade: semesterCourse.validation?.examGrade,
          finalGrade: semesterCourse.validation?.finalGrade,
          tooltipId: `course-${semesterCourse.id}`,
          toolTip:
            semesterCourse.state === CourseEnrollmentStates.Approved &&
            semesterCourse.isValidated
              ? texts.convalidationToolTip({
                  validationType:
                    semesterCourse?.validation?.validationType ??
                    CourseValidationType.Internal,
                  validationSubType:
                    semesterCourse?.validation?.validationSubType ?? null,
                  examGrade: semesterCourse.validation?.examGrade,
                  finalGrade: semesterCourse.validation?.finalGrade,
                  externalGrade: semesterCourse.validation?.externalGrade,
                  externalCourseName:
                    semesterCourse.validation?.externalCourseName,
                  code: semesterCourse.validation?.code,
                })
              : '',
          onClick,
        };
      } else {
        return {
          id: semesterCourse.id.toString(),
          name: semesterCourse.courseType.name,
          credits: semesterCourse.optionalSlot?.credits,
          color: getColor(semesterCourse.state),
          examGrade: semesterCourse.validation?.examGrade,
          finalGrade: semesterCourse.validation?.finalGrade,
          tooltipId: `optionalcourse-${semesterCourse.id}`,
          toolTip: semesterCourse?.optionalSlot
            ? texts.optionalToolTip({
                credits: semesterCourse?.validation?.totalCredits ?? 0,
                totalCredits: semesterCourse.optionalSlot?.credits,
              })
            : '',
          onClick,
        };
      }
    });
  }, [onClickProp, onClickInfoModal, semesterCourses, getColor, texts]);

  return (
    <div>
      <div className="d-block text-center mb-3 px-1">{title}</div>
      <div
        className="d-flex"
        style={{
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {courses.map((course) => {
          return <Course {...course} texts={texts} />;
        })}
        {extraCourses?.map((extraCourse) => {
          const id = Math.random().toString(36).substr(2, 9);
          return (
            <Course
              name={extraCourse.name}
              color={getColor(
                isCourseStatusType(extraCourse.state)
                  ? extraCourse.state
                  : undefined,
              )}
              texts={texts}
              replaceCredits={texts.degreeActivityType}
              tooltipId={`extracourse-${id}`}
              toolTip={texts.degreeActivityTooltip}
              id={id}
            />
          );
        })}
      </div>
    </div>
  );
}

function isCourseStatusType(value?: CourseEnrollmentStates) {
  const validStatuses = Object.values(CourseEnrollmentStates);
  return validStatuses.includes(value as CourseEnrollmentStates);
}
